import { CustomReport } from '@principle-theorem/principle-core';
import {
  DateFilterType,
  ICustomReportChartSection,
  ICustomReportColumn,
  ICustomReportFilter,
  ReportFilterComparison,
  ReportingReference,
  type ICustomReport,
  type IQueryScopeRequests,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  DAY_MONTH_YEAR_FORMAT,
  ITimePeriod,
  uid,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import moment from 'moment-timezone';
import {
  REPORT_BUILDER_DATA_SOURCES,
  resolveDataSource,
} from './data-sources/report-builder-data-sources';
import { IReportBuilderQuery } from './report-builder-query';

export interface IReportBuilderDisplay {
  columns: ICustomReportColumn[];
  chartSections: ICustomReportChartSection[];
  dynamicFilters: ICustomReportFilter[];
}

export function toCustomReport(
  base: AtLeast<ICustomReport, 'name' | 'restrictAccessTo' | 'creator'>,
  query: IReportBuilderQuery,
  display: IReportBuilderDisplay
): ICustomReport {
  return CustomReport.init({
    ...base,
    dataSource: query.dataSource,
    columns: display.columns.map((column) => ({
      uid: column.uid,
      id: column.id,
      label: column.label,
    })),
    staticFilters: compact(
      [query.staticFilters, display.dynamicFilters].flat()
    ),
    chartSections: display.chartSections,
    queryScopes: query.scopeRequests,
  });
}

export function getDateFilterLabel(
  from: moment.Moment,
  to?: moment.Moment
): string {
  return compact([
    from.format(DAY_MONTH_YEAR_FORMAT),
    to?.format(DAY_MONTH_YEAR_FORMAT),
  ]).join(' - ');
}

export function getDateRangeFilterFromQueryScopes(
  queryableTimestamp: ReportingReference
): ICustomReportFilter {
  return {
    uid: uid(),
    id: queryableTimestamp,
    comparison: ReportFilterComparison.DateIsBetween,
    dateType: DateFilterType.ReportDate,
    values: [],
  };
}

export function toCustomReportQuery(
  report: WithRef<ICustomReport>,
  scopeRequests: IQueryScopeRequests,
  filterOverrides: ICustomReportFilter[] = [],
  filterDate?: moment.Moment,
  filterDateRange?: ITimePeriod
): IReportBuilderQuery | undefined {
  const dataSource = resolveDataSource(
    report.dataSource,
    REPORT_BUILDER_DATA_SOURCES
  );
  if (!dataSource) {
    // eslint-disable-next-line no-console
    console.error(`Could not resolve data source ${report.dataSource}`);
    return;
  }

  const staticFilters = report.staticFilters.map((reportFilter) => {
    const filterOverride = filterOverrides.find(
      (override) => override.uid === reportFilter.uid
    );
    return filterOverride ?? reportFilter;
  });

  return {
    dataSource: report.dataSource,
    staticFilters,
    filterDate: filterDate ?? moment(),
    filterDateRange: filterDateRange ?? { from: moment(), to: moment() },
    columns: report.columns,
    chartSections: report.chartSections,
    scopeRequests,
    editMode: false,
    clearCurrentResults: false,
    customReportRef: report.ref,
  };
}
